import { z } from "zod";
import { CheckoutService } from "../checkout/service";
import { checkoutBody, checkoutError } from "../checkout/http";
import { OrderStockShortage } from "./schema";
import { OrdersService } from "./service";
const headers = { "Cache-Control": "no-store", "X-Content-Type-Options": "nosniff" };
const json = (value, status = 200) => Response.json(value, { status, headers });
const QuerySchema = z.object({ search: z.string().max(200).optional(), payment: z.enum(["", "Paid", "Awaiting payment", "Partially refunded", "Refunded", "Cancelled"]).optional(), delivery: z.enum(["", "Not sent", "Partly sent", "Done"]).optional(), attention: z.enum(["true", "false"]).optional(), page: z.coerce.number().int().min(0).max(100000).optional() }).strict();
/** The admin authenticates the session and checks platform ownership before dispatch. */
export async function shopOrdersRequest(request, service, checkout, actor, retryEmails) {
    const url = new URL(request.url), match = /^\/_builder\/sites\/([^/]+)\/shop\/orders(?:\/(attention\/([^/]+)\/(retry|resolve)|([^/]+)(?:\/(paid|delivery|refund|cancel|receipt))?))?$/.exec(url.pathname);
    if (!match)
        return null;
    const siteId = match[1], checkoutId = match[3], attentionAction = match[4], orderId = match[5], action = match[6];
    try {
        if (request.method === "GET" && !match[2]) {
            const query = QuerySchema.parse(Object.fromEntries(url.searchParams));
            return json(await service.index(siteId, actor, { ...query, attention: query.attention === "true" }));
        }
        if (request.method === "GET" && orderId && !action)
            return json(await service.detail(siteId, actor, orderId));
        if (request.method !== "POST")
            return json({ error: "Not found" }, 404);
        if (request.headers.get("Origin") !== url.origin || request.headers.get("Content-Type")?.split(";", 1)[0] !== "application/json")
            return json({ error: "Forbidden" }, 403);
        const input = JSON.parse(await checkoutBody(request, 32768));
        if (checkoutId) {
            if (attentionAction === "resolve") {
                const value = z.object({ note: z.string().trim().min(1).max(2000), confirmed: z.literal(true) }).strict().parse(input);
                await service.resolveAttention(siteId, actor, checkoutId, value.note);
            }
            else {
                z.object({ confirmed: z.literal(true) }).strict().parse(input);
                await checkout.retryFlagged(siteId, checkoutId, actor, c => service.assertCheckoutRetryableIn(c, siteId, checkoutId));
                await service.retryRefundsForCheckout(siteId, actor, checkoutId);
                await retryEmails?.(siteId, actor, checkoutId);
            }
            return json(await service.index(siteId, actor));
        }
        if (!orderId)
            return json({ error: "Not found" }, 404);
        switch (action) {
            case "paid": return json(await service.markPaid(siteId, actor, orderId, input));
            case "delivery": return json(await service.deliver(siteId, actor, orderId, input));
            case "refund": return json(await service.refund(siteId, actor, orderId, input));
            case "cancel": return json(await service.cancel(siteId, actor, orderId, input));
            case "receipt": return json(await service.resendReceipt(siteId, actor, orderId, input));
            default: return json({ error: "Not found" }, 404);
        }
    }
    catch (error) {
        if (error instanceof OrderStockShortage)
            return json({ error: error.message, shortages: error.shortages }, 409);
        if (error instanceof z.ZodError || error instanceof SyntaxError)
            return json({ error: "Check the order action and try again." }, 400);
        return checkoutError(error, siteId);
    }
}
